(ns manage.ad-variant.creation.feed.mapping.hooks
  (:require ["react-hook-form" :as rhf]
            [cljs-bean.core :refer [->clj ->js]]
            [common.form :as form]
            [common.queries
             :refer [use-mutation use-query use-suspense-query use-suspense-queries]]
            [common.resource.query.adset :as adset.q]
            [common.resource.query.product-store :as product-store.q]
            [common.util :as util :refer [spread-obj]]
            [manage.ad-variant.creation.content.hooks :as ch]
            [manage.ad-variant.creation.feed.mapping.util :as mapping.util :refer [automap-items]]
            [manage.ad-variant.hooks :refer [use-has-multiple-templates]]
            [manage.ad-variant.queries :as ad-variants.q]
            [manage.adset.hooks :refer [use-synced-with-hogarth?]]
            [manage.content-variant-page.queries :as content-queries]
            [uix.core :as uix :refer [defhook]]
            [uix.re-frame :refer [use-subscribe]]))

(defhook use-prepare-template-items [{:keys [countries channel synced-with-hogarth? ad-builders dimension-mapping-enabled?]}]
  (let [template-revisions (->> ad-builders
                                (filter :active)
                                (mapcat :ad-formats)
                                (mapv :current-revision))
        template-placeholders (mapping.util/get-placeholders template-revisions)
        ctv-country (first countries)
        ctv-items (if (= ctv-country "gb") mapping.util/connected-tv-bvod-items mapping.util/connected-tv-avod-items)]
    (vec (concat
          mapping.util/identifier
          (when dimension-mapping-enabled? mapping.util/template-dimensions)
          template-placeholders
          (when (= channel "meta") mapping.util/meta-items)
          (when (and (= channel "meta") synced-with-hogarth?) mapping.util/pass-through-items)
          (when (= channel "tiktok") mapping.util/tiktok-items)
          (when (= channel "youtube") mapping.util/youtube-items)
          (when (= channel "video") mapping.util/online-video-items)
          (when (= channel "ctv") ctv-items)))))

;; Code duplication to support non-suspense components
(defhook use-template-items [{:keys [dimension-mapping-enabled?]}]
  (let [{:keys [adset-id]} (use-subscribe [:url-context])
        {{:keys [channel countries]} :data} (use-query (adset.q/adset-query {:adset-id adset-id}))
        {ad-builders :data} (use-query (ad-variants.q/ad-builders-upgrade-hints-query {:adset-id adset-id}))
        synced-with-hogarth? (use-synced-with-hogarth?)]
    (use-prepare-template-items {:countries countries
                                 :channel channel
                                 :synced-with-hogarth? synced-with-hogarth?
                                 :ad-builders ad-builders
                                 :dimension-mapping-enabled? dimension-mapping-enabled?})))

(defhook use-suspense-template-items [{:keys [dimension-mapping-enabled?]}]
  (let [{:keys [adset-id]} (use-subscribe [:url-context])
        {{:keys [channel countries]} :data} (use-suspense-query (adset.q/adset-query {:adset-id adset-id}))
        {ad-builders :data} (use-suspense-query (ad-variants.q/ad-builders-upgrade-hints-query {:adset-id adset-id}))
        synced-with-hogarth? (use-synced-with-hogarth?)]
    (use-prepare-template-items {:countries countries
                                 :channel channel
                                 :synced-with-hogarth? synced-with-hogarth?
                                 :ad-builders ad-builders
                                 :dimension-mapping-enabled? dimension-mapping-enabled?})))

(defhook use-suspense-placeholders []
  (let [{:keys [adset-id]} (use-subscribe [:url-context])
        {ad-builders :data} (use-suspense-query (ad-variants.q/ad-builders-upgrade-hints-query {:adset-id adset-id}))
        template-revisions (->> ad-builders
                                (filter :active)
                                (mapcat :ad-formats)
                                (mapv :current-revision))]
    (mapping.util/get-placeholders template-revisions)))

(defn fields-select [^js fields]
  (mapv util/sanitize-fieldname fields))

(defhook use-mapping-initial-value [{:keys [feed-id multiple-dimensions?]}]
  (let [{:keys [advertiser-id adset-id]} (use-subscribe [:url-context])
        {{{:keys [field-classes field-content-mappings] :as content-feed} :data} :data}
        (use-suspense-query (content-queries/get-content-feed advertiser-id feed-id))
        {adset :data} (use-suspense-query (adset.q/adset-query {:adset-id adset-id}))
        {fields :data} (use-suspense-query (spread-obj (content-queries/get-fields advertiser-id feed-id)
                                                       #js {:select fields-select}))
        is-virtual-click-supported? (some? (#{"meta" "tiktok" "youtube" "video" "ctv"} (:channel adset)))
        dimension-mapping-enabled (or multiple-dimensions?
                                      (boolean (some #(= "template-dimensions-key" (:class %)) field-classes)))
        needs-auto-mapping? (not (ch/mapping-complete? content-feed))
        template-items (use-suspense-template-items {:dimension-mapping-enabled? dimension-mapping-enabled})
        has-product-collection-placeholders? (boolean (some #(and (= "collection" (:type %))
                                                                  (= "product" (:intent %)))
                                                            template-items))
        needs-product-store? (and needs-auto-mapping? has-product-collection-placeholders?)
        {product-store-data :data} (use-query
                                    (spread-obj (product-store.q/product-store-query
                                                 {:advertiser-id advertiser-id})
                                                #js {:enabled needs-product-store?}))
        product-store-fields (when product-store-data
                               (->> product-store-data
                                    ->clj
                                    :fields))]
    (if (ch/mapping-complete? content-feed)
      {:auto-mapped false
       :initial-dimension-mapping-enabled dimension-mapping-enabled
       :initial-value (mapping.util/->internal-mapping field-classes field-content-mappings is-virtual-click-supported?)}
      {:auto-mapped true
       :initial-dimension-mapping-enabled dimension-mapping-enabled
       :initial-value (let [parent-mappings (->> (automap-items template-items fields)
                                                 (map (fn [{:keys [name value]}]
                                                        [name {:value value}]))
                                                 (into {}))
                            auto-mapped-collection-mappings (when product-store-fields
                                                              (mapping.util/automap-child-placeholders
                                                               template-items product-store-fields))]
                        (merge parent-mappings auto-mapped-collection-mappings))})))

(defn field-mappings-select [^js fields]
  (mapv #(hash-map :from % :to (util/sanitize-fieldname %)) fields))

(defhook use-on-mapping-submit-fn [{:keys [feed-id]}]
  (let [{:keys [adset-id advertiser-id]} (use-subscribe [:url-context])

        {field-mappings :data} (use-suspense-query (spread-obj (content-queries/get-fields advertiser-id feed-id)
                                                               #js {:select field-mappings-select}))

        {{{existing-field-classes :field-classes} :data} :data}

        (use-suspense-query (content-queries/get-content-feed advertiser-id feed-id))
        {submit-mutation :mutate} (use-mutation
                                   (content-queries/submit-feed-mapping-mutation
                                    {:advertiser-id advertiser-id
                                     :adset-id adset-id
                                     :feed-id feed-id}))
        {ad-builders :data} (use-suspense-query (ad-variants.q/ad-builders-upgrade-hints-query {:adset-id adset-id}))
        {{:keys [countries channel]} :data} (use-suspense-query (adset.q/adset-query {:adset-id adset-id}))
        ctv-country (first countries)
        template-placeholders (use-suspense-placeholders)
        synced-with-hogarth? (use-synced-with-hogarth?)]
    (uix/use-callback (fn [auto-mapped? mapping]
                        (let [mappings (into {} (filter second (->clj mapping :keywordize-keys true)))
                              field-classes (mapping.util/create-field-classes mappings template-placeholders existing-field-classes)
                              all-placeholders (cond-> template-placeholders
                                                 (and (= channel "ctv") (= ctv-country "gb"))
                                                 (into mapping.util/slate-items->placeholders))
                              field-content-mappings-raw (mapping.util/create-content-mappings mappings all-placeholders)
                              ad-formats-payload (mapping.util/->ad-formats-with-products-dynamic-content-payload ad-builders field-content-mappings-raw)
                              field-content-mappings (cond-> field-content-mappings-raw
                                                       (some? (#{"meta" "tiktok" "youtube" "video"} channel))
                                                       (mapping.util/add-click-mapping field-classes)
                                                       (and synced-with-hogarth? (= channel "meta")) (mapping.util/add-pass-through-mapping mappings))
                              allow-owerriding (-> auto-mapped? true? not)]
                          ;; :TODO call endpoint to validate items ?
                          (submit-mutation {:field-mappings field-mappings
                                            :field-classes field-classes
                                            :field-content-mappings field-content-mappings
                                            :ad-formats-payload ad-formats-payload
                                            :allow-owerriding allow-owerriding})))
                      [template-placeholders field-mappings channel ctv-country ad-builders synced-with-hogarth? submit-mutation existing-field-classes])))

(defhook use-mapping-form [{:keys [feed-id]}]
  (let [{:keys [adset-id]} (use-subscribe [:url-context])
        multiple-dimensions? (use-has-multiple-templates)
        {:keys [initial-value auto-mapped initial-dimension-mapping-enabled]}
        (use-mapping-initial-value {:feed-id feed-id :multiple-dimensions? multiple-dimensions?})
        [dimension-mapping-enabled? set-dimension-mapping-enabled?]
        (uix/use-state initial-dimension-mapping-enabled)
        has-auto-mapped? (uix/use-ref false)
        on-submit-fn (use-on-mapping-submit-fn {:feed-id feed-id})
        {:keys [] :as form-props} (form/use-form #js {:mode "onChange"
                                                      :defaultValues (->js initial-value)})]
    (uix/use-effect
     (fn []
       (when (and auto-mapped (not @has-auto-mapped?))
         (reset! has-auto-mapped? true)
         (on-submit-fn true (->js initial-value))))
     [auto-mapped initial-value on-submit-fn])
    (let [on-submit (partial on-submit-fn false)
          on-submit* ((:handleSubmit (->clj form-props)) on-submit)]
      {:form-props form-props
       :on-submit* on-submit*
       :multiple-dimensions? multiple-dimensions?
       :dimension-mapping-enabled? dimension-mapping-enabled?
       :set-dimension-mapping-enabled? set-dimension-mapping-enabled?})))

(defn get-non-strategy-fields [^js result]
  (let [[{fields :data} {{{:keys [field-classes]} :data} :data}] (->clj result)
        strategy-fields (keep (fn [{:keys [class] field-name :name}]
                                (when (= mapping.util/strategy-class class)
                                  field-name))
                              field-classes)]
    #js {:non-strategy-fields (->> fields (remove (set strategy-fields)) fields-select)}))

(defhook use-field-options []
  (let [{:keys [advertiser-id adset-id]} (use-subscribe [:url-context])
        {{:keys [feedId]} :data} (use-suspense-query (adset.q/adset-query {:adset-id adset-id}))
        mapping (->clj (rhf/useWatch) :keywordize-keys true)
        {:keys [non-strategy-fields]} (use-suspense-queries
                                       #js {:queries #js [(content-queries/get-fields advertiser-id feedId)
                                                          (content-queries/get-content-feed advertiser-id feedId)]
                                            :combine get-non-strategy-fields})
        used-fields (set (keep (fn [[k v]]
                                 (when (some? k)
                                   (:value v)))
                               mapping))]
    (->> non-strategy-fields
         (mapv (fn [field]
                 {:value field :label field :disabled? false :used (contains? used-fields field)}))
         (sort-by :used))))

(defn- get-product-store-fields [^js data]
  (->> data
       ->clj
       :fields
       (map :name)))

(defhook use-collection-product-fields
  "Hook to get product store fields with collection-scoped tracking.

   Product fields are scoped per collection:
   - Same field can be used in different collections (different scopes)
   - Same field cannot be used twice in same collection

   Returns options with :used marking:
   - :used true  → field is mapped in THIS collection (disabled for selection)
   - :used false → field is unmapped (available for selection)

   Only fetches product store data for product collections (intent: 'product')."
  [collection-name collection-intent]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        is-product-collection? (= "product" collection-intent)
        {product-store-fields :data} (use-query (spread-obj (product-store.q/product-store-query
                                                             {:advertiser-id advertiser-id})
                                                            #js {:select get-product-store-fields
                                                                 :enabled is-product-collection?}))
        collection-mapping (->clj (rhf/useWatch #js {:name (str collection-name ".child-mapping")}) :keywordize-keys true)
        collection-used-fields (set (keep (fn [[child-key {:keys [value]}]]
                                            (when (and (some? child-key)
                                                       (some? value))
                                              value))
                                          collection-mapping))]
    (if product-store-fields
      (->> product-store-fields
           (mapv (fn [field]
                   {:value field
                    :label field
                    :disabled? (contains? collection-used-fields field)
                    :used (contains? collection-used-fields field)}))
           (sort-by :used))
      [])))

(defn- is-mapped?
  [mapping-value]
  (and (map? mapping-value)
       (some? (get mapping-value :value))))

;; This is hook is called not from suspense ready component
;; So there is a bit of code duplication
(defhook use-everything-mapped? []
  (let [{:keys [adset-id advertiser-id]} (use-subscribe [:url-context])
        {{:keys [channel feedId]} :data} (use-query (adset.q/adset-query {:adset-id adset-id}))
        is-virtual-click-supported? (some? (#{"meta" "tiktok" "youtube" "video" "ctv"} channel))
        {{{:keys [field-classes field-content-mappings]} :data} :data}
        (use-query (content-queries/get-content-feed advertiser-id feedId))
        multiple-dimensions? (use-has-multiple-templates)
        placeholders (use-template-items {:dimension-mapping-enabled? multiple-dimensions?})
        mapping (mapping.util/->internal-mapping field-classes field-content-mappings is-virtual-click-supported?)]
    (->> placeholders
         (filter (fn [placeholder]
                   (not (is-mapped? (get mapping (:name placeholder))))))
         count
         (= 0))))
